import { useMemo, useState } from 'react'
import { getDataAPI, postDataAPI } from '../../../lib/api'
import { importBandsintownViaAI } from '../../../lib/bandsintownAgent'
import { uploadImage } from '../../../lib/uploadImage'
import type { SignupUser } from '../SignupPage'
import OnboardingShell from './OnboardingShell'
import Card from '../../../components/ui/Card'
import { useNavigate } from 'react-router-dom'
import { OnboardingStep } from './steps'

export default function Step06Avatar({
  setStep,
  user,
  setUser,
  from,
}: {
  setStep: (n: number) => void
  user: SignupUser
  setUser: React.Dispatch<React.SetStateAction<SignupUser>>
  from: string
}) {
  const navigate = useNavigate()
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const preview = useMemo(() => {
    if (file) return URL.createObjectURL(file)
    if (typeof user.avatar === 'string') return user.avatar
    return null
  }, [file, user.avatar])

  const submit = async () => {
    setError(null)
    if (!user.email) return setError('Email is required')
    if (!user.professionRole) return setError('Role is required')
    if (!user.username) return setError('Username is required')

    setIsSubmitting(true)
    try {
      let avatarUrl: string | null = typeof user.avatar === 'string' ? user.avatar : null
      if (file) {
        avatarUrl = await uploadImage(file)
        if (!avatarUrl) throw new Error('Image upload failed')
      }

      const userToSubmit: any = { ...user, avatar: avatarUrl }
      const res = await postDataAPI('auth/add_user_info_personal', userToSubmit)
      if (res?.status !== 200) throw new Error(res?.message || 'Failed to create account')

      const createdUser = res?.data?.user
      const token = res?.data?.token
      if (token) localStorage.setItem('dizeeToken', JSON.stringify(token))
      if (createdUser) localStorage.setItem('dizeeUser', JSON.stringify(createdUser))
      localStorage.setItem('isNewSignup', 'true')

      const pendingReleasesRaw = localStorage.getItem('pendingCatalogReleases')
      if (pendingReleasesRaw) {
        try {
          const pending = JSON.parse(pendingReleasesRaw)
          const existingRes = await getDataAPI('user/getCatalog').catch(() => null)
          const existing: any[] = Array.isArray(existingRes?.data) ? existingRes.data : existingRes?.data?.data || []

          const normalize = (s: string) => (s || '').toLowerCase().trim()
          const deduped = (Array.isArray(pending) ? pending : []).filter((n: any) => {
            const nn = normalize(n.song_title)
            const na = normalize(n.primary_artist)
            if (!nn || !na) return false
            return !existing.some((e: any) => {
              const en = normalize(e.song_title || e.song_name || e.title)
              const ea = normalize(e.primary_artist || e.artist_name || e.artist)
              return nn === en && na === ea
            })
          })

          if (deduped.length) await postDataAPI('user/saveCatalog', { data: deduped })
          localStorage.removeItem('pendingCatalogReleases')
        } catch (e) {
          console.warn('Failed to save pending releases:', e)
        }
      }

      const spotifyUrl = user?.social?.sf
      if (spotifyUrl) {
        // Mark that a catalog import is in progress so the Music page can show a spinner
        localStorage.setItem('catalogImportInProgress', 'true')
        postDataAPI(
          'user/discoverCatalogFromArtist?autoSave=true',
          { spotifyUrl, artistName: user?.name || user?.username },
          undefined,
          { timeout: 300000 }, // 5 minutes for large catalogs
        )
          .then(() => {
            localStorage.removeItem('catalogImportInProgress')
          })
          .catch((err) => {
            console.warn('Onboarding catalog discovery failed:', err?.message || err)
            localStorage.removeItem('catalogImportInProgress')
          })
      }

      const pendingArtistName = localStorage.getItem('pendingBandsintownImport')
      if (pendingArtistName) {
        // Import shows via AI — they'll be stored in localStorage as pendingBandsintownShows
        // for user approval on the Shows page (instead of auto-saving directly)
        importBandsintownViaAI(pendingArtistName)
          .then((r) => {
            if (r?.success && (r as any)?.savedCount > 0) {
              localStorage.setItem('bandsintownImportSuccess', String((r as any).savedCount))
            }
          })
          .finally(() => localStorage.removeItem('pendingBandsintownImport'))
      }

      navigate(from || '/', { replace: true })
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to create account')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <OnboardingShell
      title={preview ? 'Change cover image' : 'Upload a cover image'}
      footer="Upload a profile picture that represents you or your brand. This will be displayed on your profile page."
    >
      <div className="space-y-5">
        <Card className="p-0 overflow-hidden">
          {preview ? (
            <img src={preview} alt="avatar preview" className="w-full h-56 object-cover" />
          ) : (
            <div className="w-full h-56 flex items-center justify-center text-sm text-gray-500">No image selected</div>
          )}
        </Card>

        <input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const f = e.target.files?.[0] || null
            setFile(f)
            if (f) setUser((prev) => ({ ...prev, avatar: f }))
          }}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:bg-white file:text-gray-900"
        />

        {error ? <div className="text-red-600 text-sm">{error}</div> : null}

        <div className="flex gap-4">
          <button type="button" onClick={() => setStep(OnboardingStep.AddReleases)} className="ds-btn ds-btn-secondary flex-1">
            Go back
          </button>
          <button type="button" disabled={isSubmitting} onClick={submit} className="ds-btn ds-btn-primary flex-1">
            {isSubmitting ? 'Creating…' : 'Complete'}
          </button>
        </div>
      </div>
    </OnboardingShell>
  )
}

